import { WaitingForInit } from "@src/components";
import { createRoot, Root } from "react-dom/client";
import CSS from "../styles/global.css";
import { fontDefinitions } from "./fonts";
import { isServer } from "@src/utils";

export let swapperSDKRoot: Root | null = null;
export let swapperSDKShadowRoot: ShadowRoot | null = null;
export let swapperSDKInitIndicatorRoot: Root | null = null;
export let swapperSDKTxStatusRoot: Root | null = null;

let swapperSDKInitCompleted = false;

export const initDocument = () => {
  if (isServer) {
    return;
  }
  createSwapperSDKInitIndicatorRoot();

  setupFonts();

  Promise.all([createSwapperSDKRoot(), createSwapperSDKTxStatusRoot()]).then(
    (roots) => {
      for (const root of roots) {
        attachStyles(root);
      }

      swapperSDKInitCompleted = true;
    },
  );
};

export const waitForInitialization = async () => {
  if (!swapperSDKInitCompleted) {
    displayInitIndicator(true);
    while (!swapperSDKInitCompleted) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
    displayInitIndicator(false);
  }
};

const attachStyles = async (root: ShadowRoot) => {
  const style = document.createElement("style");
  style.appendChild(document.createTextNode(CSS));
  root.appendChild(style);
};

/**
 * To let Web Components use custom fonts, we need to define them on the page's document.
 */
const setupFonts = () => {
  const SELECTOR_KEY = "swapper-sdk-fonts";

  if (document.querySelector(`style[data-name="${SELECTOR_KEY}"]`)) {
    return;
  }

  const style = document.createElement("style");
  style.dataset.name = SELECTOR_KEY;

  style.appendChild(document.createTextNode(fontDefinitions));
  document.head.appendChild(style);
};

const createSwapperSDKRoot = async () => {
  const swapperElement = document.createElement("div");
  swapperSDKShadowRoot = swapperElement.attachShadow({ mode: "open" });
  swapperElement.setAttribute("id", "swapper-sdk-root");
  document.body.appendChild(swapperElement);
  swapperSDKRoot = createRoot(swapperSDKShadowRoot);
  return swapperSDKShadowRoot;
};

const createSwapperSDKTxStatusRoot = async () => {
  const txStatusElement = document.createElement("div");
  const shadow = txStatusElement.attachShadow({ mode: "open" });
  txStatusElement.setAttribute("id", "swapper-sdk-tx-status");
  document.body.appendChild(txStatusElement);
  swapperSDKTxStatusRoot = createRoot(shadow);
  return shadow;
};

const createSwapperSDKInitIndicatorRoot = () => {
  const initIndicatorElement = document.createElement("div");
  initIndicatorElement.setAttribute("id", "swapper-sdk-init-indicator");
  document.body.appendChild(initIndicatorElement);
  swapperSDKInitIndicatorRoot = createRoot(initIndicatorElement);
};

const displayInitIndicator = (display: boolean) => {
  if (isServer || !swapperSDKInitIndicatorRoot) {
    return;
  }
  display
    ? swapperSDKInitIndicatorRoot.render(<WaitingForInit />)
    : swapperSDKInitIndicatorRoot.render(null);
};
